// AGC: Voice Queue Service
// AGC: Manages offline queue for unmatched voice commands
// AGC: Queued commands are processed when connection is restored

import type { QueuedVoiceCommand, VoiceResult } from '../types/admin'

/**
 * AGC: Get localStorage key for voice queue (user-specific)
 */
const getQueueKey = (userId: string): string => `droidfarm_voice_queue_${userId}`

/**
 * AGC: Generate unique ID for queued command
 */
const generateId = (): string => {
  return `voice_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

/**
 * AGC: Load queue from localStorage
 * @param {string} userId - User ID
 * @returns {QueuedVoiceCommand[]} Array of queued commands
 */
export const getQueuedCommands = (userId: string): QueuedVoiceCommand[] => {
  if (!userId) {
    console.warn('⚠️ AGC: No userId provided for voice queue')
    return []
  }

  try {
    const key = getQueueKey(userId)
    const stored = localStorage.getItem(key)
    
    if (stored) {
      const queue = JSON.parse(stored) as QueuedVoiceCommand[]
      console.log(`📋 AGC: Loaded ${queue.length} queued voice commands`)
      return queue
    }
  } catch (error) {
    console.error('❌ AGC: Error loading voice queue:', error)
  }
  
  return []
}

/**
 * AGC: Save queue to localStorage
 * @param {string} userId - User ID
 * @param {QueuedVoiceCommand[]} queue - Queue to save
 */
const saveQueue = (userId: string, queue: QueuedVoiceCommand[]): void => {
  if (!userId) return

  try {
    const key = getQueueKey(userId)
    localStorage.setItem(key, JSON.stringify(queue))
    console.log(`💾 AGC: Saved ${queue.length} queued voice commands`)
  } catch (error) {
    console.error('❌ AGC: Error saving voice queue:', error)
    
    // AGC: Handle quota exceeded
    if ((error as Error).name === 'QuotaExceededError') {
      console.warn('⚠️ AGC: localStorage quota exceeded - clearing old commands')
      // AGC: Remove oldest commands to make room
      const trimmedQueue = queue.slice(-10) // AGC: Keep only last 10
      const key = getQueueKey(userId)
      localStorage.setItem(key, JSON.stringify(trimmedQueue))
    }
  }
}

/**
 * AGC: Add a command to the offline queue
 * @param {string} userId - User ID
 * @param {string} text - Transcribed text
 * @param {number} maxQueueSize - Maximum queue size (default: 50)
 * @returns {QueuedVoiceCommand} The queued command
 */
export const queueUnmatchedCommand = (
  userId: string,
  text: string,
  maxQueueSize: number = 50
): QueuedVoiceCommand => {
  const queue = getQueuedCommands(userId)
  
  // AGC: Create new queued command
  const command: QueuedVoiceCommand = {
    id: generateId(),
    text: text.trim(),
    timestamp: Date.now(),
    status: 'pending',
    retryCount: 0,
    userId,
  }
  
  // AGC: Add to queue
  queue.push(command)
  
  // AGC: Trim queue if over max size (remove oldest)
  if (queue.length > maxQueueSize) {
    const removed = queue.splice(0, queue.length - maxQueueSize)
    console.log(`🗑️ AGC: Removed ${removed.length} old commands from queue (max: ${maxQueueSize})`)
  }
  
  // AGC: Save queue
  saveQueue(userId, queue)
  
  console.log(`📥 AGC: Queued voice command: "${text.substring(0, 50)}..."`)
  console.log(`📋 AGC: Queue size: ${queue.length}`)
  
  return command
}

/**
 * AGC: Get count of pending commands in queue
 * @param {string} userId - User ID
 * @returns {number} Number of pending commands
 */
export const getPendingCount = (userId: string): number => {
  const queue = getQueuedCommands(userId)
  return queue.filter(cmd => cmd.status === 'pending' || cmd.status === 'failed').length
}

/**
 * AGC: Update command status in queue
 * @param {string} userId - User ID
 * @param {string} commandId - Command ID
 * @param {Partial<QueuedVoiceCommand>} updates - Updates to apply
 */
export const updateCommandStatus = (
  userId: string,
  commandId: string,
  updates: Partial<QueuedVoiceCommand>
): void => {
  const queue = getQueuedCommands(userId)
  const index = queue.findIndex(cmd => cmd.id === commandId)
  
  if (index !== -1) {
    queue[index] = { ...queue[index], ...updates }
    saveQueue(userId, queue)
    console.log(`📝 AGC: Updated command ${commandId} status to ${updates.status}`)
  }
}

/**
 * AGC: Remove command from queue (after successful processing)
 * @param {string} userId - User ID
 * @param {string} commandId - Command ID to remove
 */
export const removeCommand = (userId: string, commandId: string): void => {
  const queue = getQueuedCommands(userId)
  const filtered = queue.filter(cmd => cmd.id !== commandId)
  
  if (filtered.length < queue.length) {
    saveQueue(userId, filtered)
    console.log(`🗑️ AGC: Removed command ${commandId} from queue`)
  }
}

/**
 * AGC: Remove multiple commands from queue
 * @param {string} userId - User ID
 * @param {string[]} commandIds - Command IDs to remove
 */
export const removeCommands = (userId: string, commandIds: string[]): void => {
  const queue = getQueuedCommands(userId)
  const filtered = queue.filter(cmd => !commandIds.includes(cmd.id))
  
  if (filtered.length < queue.length) {
    saveQueue(userId, filtered)
    console.log(`🗑️ AGC: Removed ${queue.length - filtered.length} commands from queue`)
  }
}

/**
 * AGC: Clear all completed commands from queue
 * @param {string} userId - User ID
 */
export const clearCompletedCommands = (userId: string): void => {
  const queue = getQueuedCommands(userId)
  const filtered = queue.filter(cmd => cmd.status !== 'completed')
  saveQueue(userId, filtered)
  console.log(`🧹 AGC: Cleared ${queue.length - filtered.length} completed commands`)
}

/**
 * AGC: Clear entire queue (user action)
 * @param {string} userId - User ID
 */
export const clearQueue = (userId: string): void => {
  if (!userId) return
  
  try {
    const key = getQueueKey(userId)
    localStorage.removeItem(key)
    console.log('🧹 AGC: Voice queue cleared')
  } catch (error) {
    console.error('❌ AGC: Error clearing voice queue:', error)
  }
}

/**
 * AGC: Process queued commands through backend
 * AGC: Called when connection is restored
 * @param {string} userId - User ID
 * @param {string} authToken - Authentication token
 * @param {string} backendUrl - Backend API URL
 * @returns {Promise<{processed: number, failed: number, results: VoiceResult[]}>}
 */
export const processQueuedCommands = async (
  userId: string,
  authToken: string,
  backendUrl: string
): Promise<{ processed: number; failed: number; results: VoiceResult[] }> => {
  const queue = getQueuedCommands(userId)
  const pendingCommands = queue.filter(cmd => 
    cmd.status === 'pending' || 
    (cmd.status === 'failed' && cmd.retryCount < 3)
  )
  
  if (pendingCommands.length === 0) {
    console.log('📋 AGC: No pending commands to process')
    return { processed: 0, failed: 0, results: [] }
  }
  
  console.log(`🔄 AGC: Processing ${pendingCommands.length} queued commands...`)
  
  let processed = 0
  let failed = 0
  const results: VoiceResult[] = []
  
  for (const command of pendingCommands) {
    // AGC: Mark as processing
    updateCommandStatus(userId, command.id, { status: 'processing' })
    
    try {
      // AGC: Send to backend for GPT processing
      const response = await fetch(`${backendUrl}/api/voice/process-text`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: command.text,
          userId,
          queuedAt: command.timestamp,
        }),
      })
      
      if (response.ok) {
        const result = await response.json() as VoiceResult
        results.push(result)
        
        if (result.success) {
          // AGC: Mark as completed and remove
          removeCommand(userId, command.id)
          processed++
          console.log(`✅ AGC: Processed queued command: "${command.text.substring(0, 30)}..."`)
        } else {
          // AGC: Mark as failed
          updateCommandStatus(userId, command.id, {
            status: 'failed',
            retryCount: command.retryCount + 1,
            errorMessage: result.message,
          })
          failed++
        }
      } else {
        // AGC: HTTP error - mark as failed
        updateCommandStatus(userId, command.id, {
          status: 'failed',
          retryCount: command.retryCount + 1,
          errorMessage: `HTTP ${response.status}`,
        })
        failed++
      }
    } catch (error) {
      // AGC: Network error - mark as failed (will retry)
      console.error('❌ AGC: Error processing queued command:', error)
      updateCommandStatus(userId, command.id, {
        status: 'failed',
        retryCount: command.retryCount + 1,
        errorMessage: (error as Error).message,
      })
      failed++
    }
  }
  
  console.log(`📊 AGC: Queue processing complete: ${processed} processed, ${failed} failed`)
  
  return { processed, failed, results }
}

/**
 * AGC: Get queue statistics
 * @param {string} userId - User ID
 * @returns {Object} Queue statistics
 */
export const getQueueStats = (userId: string): {
  total: number
  pending: number
  failed: number
  processing: number
  oldestTimestamp: number | null
} => {
  const queue = getQueuedCommands(userId)
  
  return {
    total: queue.length,
    pending: queue.filter(cmd => cmd.status === 'pending').length,
    failed: queue.filter(cmd => cmd.status === 'failed').length,
    processing: queue.filter(cmd => cmd.status === 'processing').length,
    oldestTimestamp: queue.length > 0 
      ? Math.min(...queue.map(cmd => cmd.timestamp))
      : null,
  }
}

export default {
  getQueuedCommands,
  queueUnmatchedCommand,
  getPendingCount,
  updateCommandStatus,
  removeCommand,
  removeCommands,
  clearCompletedCommands,
  clearQueue,
  processQueuedCommands,
  getQueueStats,
}





